import { HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";
import { GlobalRequestPrefix } from "src/app/core/constants";
import { PaginationV2 } from "src/app/core/pagination-v2";
import {
  PatchRequestData,
  PatchRequestOptions,
  PostRequestData,
  PutRequestData,
  RequestV2Service,
} from "src/app/core/request-v2.service";
import { Site, SitesService } from "src/app/core/sites";
import { PartnersService } from "src/app/partner/global/partners.service";
import {
  ApiUrl,
  OperationName,
  OperationResponse,
  PathParamsOfOperation,
  QueryParamsOfOperation,
} from "src/utils/resource-url-v2/resource-url-v2-types";

/**
 * The prefix strategy to use when resolving the API URL prefix.
 *
 * - `"global"` resolves to `GlobalRequestPrefix` (`"/common"`).
 * - `"site"` resolves to `site.getApiUrl()` from the currently selected site.
 * - `"partner"` resolves to `site.partner.getApiUrl()` from the currently selected partner.
 */
type RequestPrefix = "global" | "site" | "partner";

type SiteResponseMapper<Response, Mapped> = (
  response: Response,
  site: Site,
) => Mapped;

type SiteRequestData<T> = T | ((site: Site) => T);

interface GetRequestArgs<
  PathName extends ApiUrl,
  Method extends OperationName,
> {
  prefix: RequestPrefix;
  path: PathName;
  pathParams?: PathParamsOfOperation<PathName, Method>;
  queryParams?: QueryParamsOfOperation<PathName, Method>;
  headers?: HttpHeaders;
  /** For callers that already re-request on `SitesService.selectedChanges`. */
  shouldResolvePrefixOnce?: boolean;
}

interface MappedGetRequestArgs<
  PathName extends ApiUrl,
  Method extends OperationName,
  Mapped,
> extends GetRequestArgs<PathName, Method> {
  mapResponse: SiteResponseMapper<OperationResponse<PathName, Method>, Mapped>;
}

interface GetPagedRequestArgs<PathName extends ApiUrl> {
  prefix: RequestPrefix;
  path: PathName;
  pathParams?: PathParamsOfOperation<PathName, "post">;
  queryParams?: QueryParamsOfOperation<PathName, "post">;
  data: SiteRequestData<PostRequestData<PathName>>;
  pagination: PaginationV2;
  shouldPreserveInitialPagination?: boolean;
}

interface MappedGetPagedRequestArgs<
  PathName extends ApiUrl,
  Mapped,
> extends GetPagedRequestArgs<PathName> {
  mapResponse: SiteResponseMapper<OperationResponse<PathName, "post">, Mapped>;
}

interface PostRequestArgs<
  PathName extends ApiUrl,
  Method extends OperationName,
> {
  prefix: RequestPrefix;
  path: PathName;
  pathParams?: PathParamsOfOperation<PathName, Method>;
  queryParams?: QueryParamsOfOperation<PathName, Method>;
  data: SiteRequestData<PostRequestData<PathName>>;
}

interface MappedPostRequestArgs<
  PathName extends ApiUrl,
  Method extends OperationName,
  Mapped,
> extends PostRequestArgs<PathName, Method> {
  mapResponse: SiteResponseMapper<OperationResponse<PathName, Method>, Mapped>;
}

interface PutRequestArgs<
  PathName extends ApiUrl,
  Method extends OperationName,
> {
  prefix: RequestPrefix;
  path: PathName;
  pathParams?: PathParamsOfOperation<PathName, Method>;
  queryParams?: QueryParamsOfOperation<PathName, Method>;
  data: SiteRequestData<PutRequestData<PathName>>;
}

interface MappedPutRequestArgs<
  PathName extends ApiUrl,
  Method extends OperationName,
  Mapped,
> extends PutRequestArgs<PathName, Method> {
  mapResponse: SiteResponseMapper<OperationResponse<PathName, Method>, Mapped>;
}

/**
 * A thin wrapper around {@link RequestV2Service} that automatically resolves
 * the API URL prefix based on a {@link RequestPrefix} strategy, removing the
 * need for consumers to manually subscribe to `SitesService.selectedChanges`
 * or `PartnersService.selectedChanges`.
 */
@Injectable({ providedIn: "root" })
export class PrefixedRequestService {
  public constructor(
    private readonly requestV2: RequestV2Service,
    private readonly sites: SitesService,
    private readonly partners: PartnersService,
  ) {}

  public get<
    PathName extends ApiUrl,
    Method extends OperationName = "get",
    Mapped = OperationResponse<PathName, Method>,
  >(
    args: MappedGetRequestArgs<PathName, Method, Mapped>,
  ): Observable<Mapped | null>;

  public get<PathName extends ApiUrl, Method extends OperationName = "get">(
    args: GetRequestArgs<PathName, Method>,
  ): Observable<OperationResponse<PathName, Method> | null>;

  public get<
    PathName extends ApiUrl,
    Method extends OperationName = "get",
    Mapped = OperationResponse<PathName, Method>,
  >({
    prefix,
    path,
    pathParams,
    queryParams,
    headers,
    mapResponse,
    shouldResolvePrefixOnce = false,
  }: GetRequestArgs<PathName, Method> & {
    mapResponse?: SiteResponseMapper<
      OperationResponse<PathName, Method>,
      Mapped
    >;
  }): Observable<OperationResponse<PathName, Method> | Mapped | null> {
    return this.resolvePrefixContext(prefix, {
      shouldTakeOnce: shouldResolvePrefixOnce,
    }).pipe(
      switchMap(({ resolvedPrefix, site }) =>
        this.requestV2
          .get<PathName, Method>({
            prefix: resolvedPrefix,
            path,
            pathParams,
            queryParams,
            headers,
          })
          .pipe(
            map((response) =>
              this.mapNullableSiteResponse(response, site, mapResponse),
            ),
          ),
      ),
    );
  }

  public getBlob<
    PathName extends ApiUrl,
    Method extends OperationName = "get",
  >({
    prefix,
    path,
    pathParams,
    queryParams,
  }: {
    prefix: RequestPrefix;
    path: PathName;
    pathParams?: PathParamsOfOperation<PathName, Method>;
    queryParams?: QueryParamsOfOperation<PathName, Method>;
  }): Observable<Blob | null> {
    return this.resolvePrefix(prefix).pipe(
      switchMap((resolvedPrefix) =>
        this.requestV2.getBlob<PathName, Method>({
          prefix: resolvedPrefix,
          path,
          pathParams,
          queryParams,
        }),
      ),
    );
  }

  public getPaged<
    PathName extends ApiUrl,
    Mapped = OperationResponse<PathName, "post">,
  >(
    args: MappedGetPagedRequestArgs<PathName, Mapped>,
  ): Observable<Mapped | null>;

  public getPaged<PathName extends ApiUrl>(
    args: GetPagedRequestArgs<PathName>,
  ): Observable<OperationResponse<PathName, "post"> | null>;

  public getPaged<
    PathName extends ApiUrl,
    Mapped = OperationResponse<PathName, "post">,
  >({
    prefix,
    path,
    pathParams,
    queryParams,
    data,
    pagination,
    shouldPreserveInitialPagination,
    mapResponse,
  }: GetPagedRequestArgs<PathName> & {
    mapResponse?: SiteResponseMapper<
      OperationResponse<PathName, "post">,
      Mapped
    >;
  }): Observable<OperationResponse<PathName, "post"> | Mapped | null> {
    return this.resolvePrefixContext(prefix).pipe(
      switchMap(({ resolvedPrefix, site }) =>
        this.requestV2
          .getPaged({
            prefix: resolvedPrefix,
            path,
            pathParams,
            queryParams,
            data: this.resolveSiteRequestData(data, site),
            pagination,
            shouldPreserveInitialPagination,
          })
          .pipe(
            map((response) =>
              this.mapNullableSiteResponse(response, site, mapResponse),
            ),
          ),
      ),
    );
  }

  public post<
    PathName extends ApiUrl,
    Method extends OperationName = "post",
    Mapped = OperationResponse<PathName, Method>,
  >(
    args: MappedPostRequestArgs<PathName, Method, Mapped>,
  ): Observable<Mapped | null>;

  public post<PathName extends ApiUrl, Method extends OperationName = "post">(
    args: PostRequestArgs<PathName, Method>,
  ): Observable<OperationResponse<PathName, Method> | null>;

  public post<
    PathName extends ApiUrl,
    Method extends OperationName = "post",
    Mapped = OperationResponse<PathName, Method>,
  >({
    prefix,
    path,
    pathParams,
    queryParams,
    data,
    mapResponse,
  }: PostRequestArgs<PathName, Method> & {
    mapResponse?: SiteResponseMapper<
      OperationResponse<PathName, Method>,
      Mapped
    >;
  }): Observable<OperationResponse<PathName, Method> | Mapped | null> {
    return this.resolvePrefixContext(prefix, { shouldTakeOnce: true }).pipe(
      switchMap(({ resolvedPrefix, site }) =>
        this.requestV2
          .post<PathName, Method>({
            prefix: resolvedPrefix,
            path,
            pathParams,
            queryParams,
            data: this.resolveSiteRequestData(data, site),
          })
          .pipe(
            map((response) =>
              this.mapNullableSiteResponse(response, site, mapResponse),
            ),
          ),
      ),
    );
  }

  public put<
    PathName extends ApiUrl,
    Method extends OperationName = "put",
    Mapped = OperationResponse<PathName, Method>,
  >(args: MappedPutRequestArgs<PathName, Method, Mapped>): Observable<Mapped>;

  public put<PathName extends ApiUrl, Method extends OperationName = "put">(
    args: PutRequestArgs<PathName, Method>,
  ): Observable<OperationResponse<PathName, Method>>;

  public put<
    PathName extends ApiUrl,
    Method extends OperationName = "put",
    Mapped = OperationResponse<PathName, Method>,
  >({
    prefix,
    path,
    pathParams,
    queryParams,
    data,
    mapResponse,
  }: PutRequestArgs<PathName, Method> & {
    mapResponse?: SiteResponseMapper<
      OperationResponse<PathName, Method>,
      Mapped
    >;
  }): Observable<OperationResponse<PathName, Method> | Mapped> {
    return this.resolvePrefixContext(prefix, { shouldTakeOnce: true }).pipe(
      switchMap(({ resolvedPrefix, site }) =>
        this.requestV2
          .put<PathName, Method>({
            prefix: resolvedPrefix,
            path,
            pathParams,
            queryParams,
            data: this.resolveSiteRequestData(data, site),
          })
          .pipe(
            map((response) =>
              this.mapSiteResponse(response, site, mapResponse),
            ),
          ),
      ),
    );
  }

  public patch<
    PathName extends ApiUrl,
    Method extends OperationName = "patch",
  >({
    prefix,
    path,
    pathParams,
    data,
    options,
  }: {
    prefix: RequestPrefix;
    path: PathName;
    pathParams?: PathParamsOfOperation<PathName, Method>;
    data: PatchRequestData<PathName> | null;
    options?: PatchRequestOptions;
  }): Observable<OperationResponse<PathName, Method>> {
    return this.resolvePrefix(prefix, { shouldTakeOnce: true }).pipe(
      switchMap((resolvedPrefix) =>
        this.requestV2.patch<PathName, Method>({
          prefix: resolvedPrefix,
          path,
          pathParams,
          data,
          options,
        }),
      ),
    );
  }

  public delete<
    PathName extends ApiUrl,
    Method extends OperationName = "delete",
  >({
    prefix,
    path,
    pathParams,
    queryParams,
  }: {
    prefix: RequestPrefix;
    path: PathName;
    pathParams?: PathParamsOfOperation<PathName, Method>;
    queryParams?: QueryParamsOfOperation<PathName, Method>;
  }): Observable<OperationResponse<PathName, Method>> {
    return this.resolvePrefix(prefix, { shouldTakeOnce: true }).pipe(
      switchMap((resolvedPrefix) =>
        this.requestV2.delete<PathName, Method>({
          prefix: resolvedPrefix,
          path,
          pathParams,
          queryParams,
        }),
      ),
    );
  }

  private resolvePrefix(
    prefix: RequestPrefix,
    options: { shouldTakeOnce?: boolean } = {},
  ): Observable<string> {
    return this.resolvePrefixContext(prefix, options).pipe(
      map(({ resolvedPrefix }) => resolvedPrefix),
    );
  }

  private resolvePrefixContext(
    prefix: RequestPrefix,
    { shouldTakeOnce = false }: { shouldTakeOnce?: boolean } = {},
  ): Observable<{ resolvedPrefix: string; site: Site | null }> {
    switch (prefix) {
      case "global":
        return of({ resolvedPrefix: GlobalRequestPrefix, site: null });
      case "site": {
        const selectedChanges = this.sites.selectedChanges.pipe(
          map((site) => ({ resolvedPrefix: site.getApiUrl(), site })),
        );

        return shouldTakeOnce ? selectedChanges.pipe(take(1)) : selectedChanges;
      }
      case "partner": {
        const selectedChanges = this.partners.selectedChanges.pipe(
          map((partner) => ({
            resolvedPrefix: partner.getApiUrl(),
            site: null,
          })),
        );

        return shouldTakeOnce ? selectedChanges.pipe(take(1)) : selectedChanges;
      }
    }
  }

  private resolveSiteRequestData<T>(
    data: SiteRequestData<T>,
    site: Site | null,
  ): T {
    if (typeof data !== "function") {
      return data;
    }

    if (!site) {
      throw new Error(
        "Cannot resolve site request data without a selected site.",
      );
    }

    return (data as (site: Site) => T)(site);
  }

  private mapNullableSiteResponse<Response, Mapped = Response>(
    response: Response | null,
    site: Site | null,
    mapResponse?: SiteResponseMapper<Response, Mapped>,
  ): Mapped | Response | null {
    return response === null
      ? null
      : this.mapSiteResponse(response, site, mapResponse);
  }

  private mapSiteResponse<Response, Mapped = Response>(
    response: Response,
    site: Site | null,
    mapResponse?: SiteResponseMapper<Response, Mapped>,
  ): Mapped | Response {
    if (!mapResponse) {
      return response;
    }

    if (!site) {
      throw new Error(
        "Cannot map response with site context without a selected site.",
      );
    }

    return mapResponse(response, site);
  }
}
